import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { HttpService } from './http.service';

import {
  CreateUserRequest,
  DeletedUserResponse,
  UpdateUserRequest,
  UserResponse,
  UserStatistics,
  UserWithRolesResponse,
  VerifyKycRequest,
} from '../constants/models/user.model';

@Injectable({ providedIn: 'root' })
export class UserService {

  private readonly http = inject(HttpService);

  // =========================================
  // PROFILE
  // =========================================

  getProfile(): Observable<UserResponse> {
    return this.http.get<UserResponse>('/users/me');
  }

  getMyRoles(): Observable<UserWithRolesResponse> {
    return this.http.get<UserWithRolesResponse>('/users/me/roles');
  }

  updateProfile(
    id: string,
    request: UpdateUserRequest
  ): Observable<UserResponse> {
    return this.http.put<UserResponse>(`/users/${id}`, request);
  }

  // =========================================
  // USERS
  // =========================================

  getAll(page = 0, size = 20): Observable<{ users: UserResponse[]; total: number }> {
    return this.http.getWithHeaders<UserResponse[]>('/users', { page, size }).pipe(
      map(res => ({
        users: res.body ?? [],
        total: Number(res.headers.get('X-Total-Count') ?? 0),
      }))
    );
  }

  getById(id: string): Observable<UserResponse> {
    return this.http.get<UserResponse>(`/users/${id}`);
  }

  getByEmail(email: string): Observable<UserResponse> {
    return this.http.get<UserResponse>('/users/email', { email });
  }

  search(q: string, limit = 20): Observable<UserResponse[]> {
    return this.http.get<UserResponse[]>('/users/search', { q, limit });
  }

  getWithRoles(id: string): Observable<UserWithRolesResponse> {
    return this.http.get<UserWithRolesResponse>(`/users/${id}/roles`);
  }

  getStaff(page = 0, size = 20): Observable<{ users: UserWithRolesResponse[]; total: number }> {
    return this.http.getWithHeaders<UserWithRolesResponse[]>('/users/staff', { page, size }).pipe(
      map(res => ({
        users: res.body ?? [],
        total: Number(res.headers.get('X-Total-Count') ?? 0),
      }))
    );
  }

  filterByAccountStatus(status: string, page = 0, size = 20): Observable<{ users: UserResponse[]; total: number }> {
    return this.http.getWithHeaders<UserResponse[]>('/users/filter/account-status', { status, page, size }).pipe(
      map(res => ({
        users: res.body ?? [],
        total: Number(res.headers.get('X-Total-Count') ?? 0),
      }))
    );
  }

  filterByKycStatus(status: string, page = 0, size = 20): Observable<{ users: UserResponse[]; total: number }> {
    return this.http.getWithHeaders<UserResponse[]>('/users/filter/kyc-status', { status, page, size }).pipe(
      map(res => ({
        users: res.body ?? [],
        total: Number(res.headers.get('X-Total-Count') ?? 0),
      }))
    );
  }

  create(request: CreateUserRequest): Observable<UserResponse> {
    return this.http.post<UserResponse>('/users', request);
  }

  notifyAccountCreated(id: string): Observable<void> {
    return this.http.post<void>(`/users/${id}/notify-account-created`);
  }

  update(id: string, request: UpdateUserRequest): Observable<UserResponse> {
    return this.http.put<UserResponse>(`/users/${id}`, request);
  }

  delete(id: string, reason?: string): Observable<void> {
    return this.http.delete<void>(`/users/${id}`, reason ? { reason } : undefined);
  }

  /**
   * Suppression d'un compte staff — le mot de passe de l'acteur est requis par le backend
   * dès que la cible a au moins un rôle != USER.
   */
  deleteStaff(id: string, password: string, reason?: string): Observable<void> {
    return this.http.delete<void>(`/users/${id}`, { password, ...(reason ? { reason } : {}) });
  }

  verifyKyc(id: string, request: VerifyKycRequest): Observable<UserResponse> {
    return this.http.post<UserResponse>(`/users/${id}/kyc/verify`, request);
  }

  rejectKyc(id: string, reason: string, password: string): Observable<UserResponse> {
    return this.http.post<UserResponse>(`/users/${id}/kyc/reject`, { reason, password });
  }

  activate(id: string): Observable<UserResponse> {
    return this.http.post<UserResponse>(`/users/${id}/activate`);
  }

  suspend(id: string): Observable<UserResponse> {
    return this.http.post<UserResponse>(`/users/${id}/suspend`);
  }

  close(id: string): Observable<UserResponse> {
    return this.http.post<UserResponse>(`/users/${id}/close`);
  }

  assignRole(id: string, roleName: string): Observable<void> {
    return this.http.post<void>(`/users/${id}/roles`, { roleName });
  }

  assignMultipleRoles(id: string, roleNames: string[]): Observable<void> {
    return this.http.post<void>(`/users/${id}/roles/batch`, { roleNames });
  }

  removeRole(id: string, roleName: string): Observable<void> {
    return this.http.delete<void>(`/users/${id}/roles/${roleName}`);
  }

  getStatistics(): Observable<UserStatistics> {
    return this.http.get<UserStatistics>('/users/statistics');
  }

  // =========================================
  // DELETED USERS
  // =========================================

  getDeletedUsers(page = 0, size = 20): Observable<{ users: DeletedUserResponse[]; total: number }> {
    return this.http.getWithHeaders<DeletedUserResponse[]>('/users/deleted', { page, size }).pipe(
      map(res => ({
        users: res.body ?? [],
        total: Number(res.headers.get('X-Total-Count') ?? 0),
      }))
    );
  }

  restore(originalUserId: string): Observable<UserResponse> {
    return this.http.patch<UserResponse>(`/users/${originalUserId}/restore`);
  }
}